import { useMemo, type ReactNode } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { MATCHDAY_COUNT, SWAP_MATCHDAY } from '@/constants';
import { MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { ListScoreBreakdown } from '@/components/ListScoreBreakdown';
import { buildIdealListRows } from '@/helpers/analytics/computeIdealList';
import { buildCPM, buildMPM } from '@/helpers/scoring/matchdayScoring';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';
import { useProgressionAwards } from '@/hooks/useProgressionAwards';
import type { Country } from '@/models/country';
import type { gameResult } from '@/models/gameUpdate';

export type MyListScoreBreakdownModalProps = {
  isOpen: boolean;
  onClose: () => void;
  tournamentStarted: boolean;
  /** Base (pre-swap) ranked list. */
  countryList: Country[];
  swapPair: [number, number] | null;
  mdReset?: number;
  gameResults: gameResult[];
  currentMatchday: number;
  matchdayCount?: number;
  swapMatchday?: number;
};

export function MyListScoreBreakdownModal(props: MyListScoreBreakdownModalProps) {
  const {
    isOpen,
    onClose,
    tournamentStarted,
    countryList,
    swapPair,
    mdReset = 0,
    gameResults,
    currentMatchday,
    matchdayCount = MATCHDAY_COUNT,
    swapMatchday = SWAP_MATCHDAY,
  } = props;
  const { isMobile, isCentered, scrollBehavior, contentProps, footerPb } = useMobileFormModal();
  const progressionAwards = useProgressionAwards();

  const breakdown = useMemo(() => {
    if (!tournamentStarted || countryList.length !== 10) return null;

    const mpm = buildMPM(gameResults, matchdayCount, progressionAwards);
    const cpm = buildCPM(mpm, matchdayCount);
    const rows = buildIdealListRows({
      countryList,
      swapPair,
      asOfMatchday: currentMatchday,
      cpm,
      swapMatchday,
      mdReset,
    });
    const score = rows.reduce((sum, row) => sum + row.contribution, 0);
    return { rows, score };
  }, [
    tournamentStarted,
    countryList,
    swapPair,
    mdReset,
    gameResults,
    currentMatchday,
    matchdayCount,
    swapMatchday,
    progressionAwards,
  ]);

  let body: ReactNode;
  if (!tournamentStarted) {
    body = (
      <Text fontSize='sm' color='gray.500' textAlign='center' py={6}>
        Your score breakdown will appear here once the tournament starts and MatchDays are
        settled.
      </Text>
    );
  } else if (countryList.length !== 10) {
    body = (
      <Text fontSize='sm' color='gray.500' textAlign='center' py={6}>
        Create your 10-team list to see how each team contributes to your points.
      </Text>
    );
  } else if (breakdown) {
    body = (
      <ListScoreBreakdown
        score={breakdown.score}
        swapPair={swapPair}
        countryList={countryList}
        rows={breakdown.rows}
        showArithmetic
        swapLabel='Swap'
        compact={isMobile}
      />
    );
  } else {
    body = null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={isCentered}
      size={isMobile ? 'full' : '2xl'}
      scrollBehavior={scrollBehavior}
    >
      <ModalOverlay />
      <ModalContent {...contentProps} maxW={isMobile ? undefined : '42rem'}>
        <ModalHeader
          pt={MODAL_HEADER_PT}
          fontSize={{ base: 'lg', md: 'xl' }}
          textAlign='center'
          pr={10}
          pl={10}
        >
          My Points Arithmetic
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody pb={footerPb}>{body}</ModalBody>
        <ModalFooter pb={footerPb}>
          <Button variant='ghost' onClick={onClose} w='full'>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
